import { useState } from "react";
import AreaChart from "@/components/feature/AreaChart";
import {
  reportSummary,
  dailyRevenue,
  dailyOrders,
  orderStats,
  reportOrders,
} from "@/mocks/reports";

const periods = ["هذا الأسبوع", "هذا الشهر", "3 شهور", "مخصص"];

export default function Reports() {
  const [period, setPeriod] = useState("هذا الشهر");
  const [sortKey, setSortKey] = useState<keyof (typeof reportOrders)[number] | null>(null);
  const [sortAsc, setSortAsc] = useState(true);

  const maxOrders = Math.max(...dailyOrders.map((d) => d.completed + d.cancelled), 1);

  const sortedRows = [...reportOrders].sort((a, b) => {
    if (!sortKey) return 0;
    const va = Number(a[sortKey].toString().replace(/[^\d.]/g, "")) || 0;
    const vb = Number(b[sortKey].toString().replace(/[^\d.]/g, "")) || 0;
    return sortAsc ? va - vb : vb - va;
  });

  const toggleSort = (key: keyof (typeof reportOrders)[number]) => {
    if (sortKey === key) {
      setSortAsc(!sortAsc);
    } else {
      setSortKey(key);
      setSortAsc(true);
    }
  };

  return (
    <div>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-[28px] font-bold text-ghasla-text-heading">التقارير المالية</h1>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            className="flex cursor-pointer items-center gap-2 rounded-xl border border-[#0D6E6E] px-4 py-2.5 text-sm font-medium text-ghasla-text-link hover:bg-[#F0FDFD]"
          >
            <i className="ri-file-pdf-2-line" />
            تصدير PDF
          </button>
          <button
            type="button"
            className="flex cursor-pointer items-center gap-2 rounded-xl border border-[#0D6E6E] px-4 py-2.5 text-sm font-medium text-ghasla-text-link hover:bg-[#F0FDFD]"
          >
            <i className="ri-file-excel-2-line" />
            تصدير Excel
          </button>
        </div>
      </div>

      {/* Period selector */}
      <div className="mt-5 flex flex-wrap items-center gap-1 rounded-full bg-white p-1 shadow-card sm:w-fit">
        {periods.map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => setPeriod(p)}
            className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
              period === p ? "bg-[#0D6E6E] text-white" : "text-ghasla-text-secondary hover:text-ghasla-text-link"
            }`}
          >
            {p}
          </button>
        ))}
      </div>
      {period === "مخصص" && (
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <div className="flex h-11 items-center gap-2 rounded-xl border border-[#E4EFEF] bg-white px-3 text-sm text-ghasla-text-secondary">
            <i className="ri-calendar-line" />
            <span className="whitespace-nowrap">من</span>
            <input
              type="date"
              className="cursor-pointer bg-transparent text-sm text-ghasla-text-primary outline-none"
            />
          </div>
          <div className="flex h-11 items-center gap-2 rounded-xl border border-[#E4EFEF] bg-white px-3 text-sm text-ghasla-text-secondary">
            <i className="ri-calendar-line" />
            <span className="whitespace-nowrap">إلى</span>
            <input
              type="date"
              className="cursor-pointer bg-transparent text-sm text-ghasla-text-primary outline-none"
            />
          </div>
        </div>
      )}

      {/* Summary cards */}
      <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-4">
        {reportSummary.map((s) => (
          <div key={s.label} className="rounded-[20px] bg-white p-6 shadow-card">
            <p className="text-sm text-ghasla-text-secondary">{s.label}</p>
            <p
              className={`mt-2 text-[28px] font-bold leading-none ${
                s.tone === "danger"
                  ? "text-[#E74C3C]"
                  : s.tone === "highlight"
                    ? "text-ghasla-text-link"
                    : "text-ghasla-text-heading"
              }`}
            >
              {s.value}
            </p>
            <p
              className={`mt-2 text-[13px] font-medium ${
                s.tone === "danger"
                  ? "text-[#E74C3C]"
                  : s.note.startsWith("↑")
                    ? "text-[#27AE60]"
                    : "text-ghasla-text-secondary"
              }`}
            >
              {s.note}
            </p>
          </div>
        ))}
      </div>

      {/* Revenue chart */}
      <div className="mt-5 rounded-[20px] bg-white p-6 shadow-card">
        <h2 className="text-lg font-semibold text-ghasla-text-heading">الإيرادات اليومية</h2>
        <div className="mt-6">
          <AreaChart
            data={dailyRevenue.map((d) => ({ label: d.day, value: d.amount }))}
            valueSuffix=" ر.س"
            tooltip
          />
        </div>
      </div>

      {/* Orders breakdown */}
      <div className="mt-5 grid grid-cols-1 gap-5 lg:grid-cols-2">
        <div className="rounded-[20px] bg-white p-6 shadow-card">
          <h2 className="text-lg font-semibold text-ghasla-text-heading">الطلبات</h2>
          <div className="mt-6 flex items-end gap-2" style={{ height: 180 }}>
            {dailyOrders.map((d) => (
              <div key={d.day} className="flex h-full flex-1 flex-col items-center justify-end gap-2">
                <div className="flex w-full max-w-[34px] flex-col-reverse overflow-hidden rounded-t-lg">
                  <div className="w-full bg-[#27AE60]" style={{ height: `${(d.completed / maxOrders) * 140}px` }} />
                  <div className="w-full bg-[#E74C3C]" style={{ height: `${(d.cancelled / maxOrders) * 140}px` }} />
                </div>
                <span className="whitespace-nowrap text-[10px] text-ghasla-text-secondary">{d.day.split(" ")[0]}</span>
              </div>
            ))}
          </div>
          <div className="mt-4 flex items-center gap-5 text-xs text-ghasla-text-secondary">
            <span className="flex items-center gap-1.5">
              <span className="h-2.5 w-2.5 rounded-full bg-[#27AE60]" />
              مكتمل
            </span>
            <span className="flex items-center gap-1.5">
              <span className="h-2.5 w-2.5 rounded-full bg-[#E74C3C]" />
              ملغي
            </span>
          </div>
        </div>

        <div className="rounded-[20px] bg-white p-6 shadow-card">
          <h2 className="text-lg font-semibold text-ghasla-text-heading">إحصائيات الطلبات</h2>
          <div className="mt-4 space-y-3 text-sm">
            <Stat label="إجمالي الطلبات" value={`${orderStats.total}`} />
            <Stat label="مكتمل" value={`${orderStats.completed} (${orderStats.completedPct}%)`} color="#27AE60" />
            <Stat label="ملغي" value={`${orderStats.cancelled} (${orderStats.cancelledPct}%)`} color="#E74C3C" />
            <Stat label="مرفوض" value={`${orderStats.rejected} (${orderStats.rejectedPct}%)`} color="#F59E0B" />
            <Stat label="متوسط قيمة الطلب" value={orderStats.avgValue} />
            <Stat label="أعلى خدمة" value={orderStats.topService} />
          </div>
        </div>
      </div>

      {/* Detailed table */}
      <div className="mt-5 overflow-hidden rounded-[20px] bg-white shadow-card">
        <div className="px-6 pt-6">
          <h2 className="text-lg font-semibold text-ghasla-text-heading">تفاصيل الطلبات</h2>
        </div>
        <div className="overflow-x-auto p-6">
          <table className="w-full min-w-[560px] text-sm">
            <thead>
              <tr className="text-xs font-semibold text-ghasla-text-secondary">
                {(["id", "date", "service", "amount", "commission", "net"] as const).map((k) => (
                  <th key={k} className="h-11 px-3 text-right">
                    <button
                      type="button"
                      onClick={() => toggleSort(k)}
                      className="cursor-pointer whitespace-nowrap hover:text-ghasla-text-link"
                    >
                      {k === "id" && "رقم الطلب"}
                      {k === "date" && "التاريخ"}
                      {k === "service" && "الخدمة"}
                      {k === "amount" && "المبلغ"}
                      {k === "commission" && "العمولة"}
                      {k === "net" && "الصافي"}
                      <i className="ri-arrow-up-down-line mr-1" />
                    </button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sortedRows.map((o) => (
                <tr key={o.id} className="border-t border-[#F4F8F8] hover:bg-[#FAFEFE]">
                  <td className="h-12 px-3 font-medium text-ghasla-text-link">{o.id}</td>
                  <td className="h-12 px-3 text-ghasla-text-primary">{o.date}</td>
                  <td className="h-12 px-3 text-ghasla-text-primary">{o.service}</td>
                  <td className="h-12 px-3 font-medium text-ghasla-text-heading">{o.amount} ر.س</td>
                  <td className="h-12 px-3 text-[#E74C3C]">-{o.commission}</td>
                  <td className="h-12 px-3 font-semibold text-[#27AE60]">{o.net} ر.س</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between border-t border-[#F4F8F8] px-6 py-4">
          <span className="text-sm text-ghasla-text-secondary">عرض 1-8 من 147 طلب</span>
          <div className="flex items-center gap-2">
            <button type="button" className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-lg border border-[#E4EFEF] text-ghasla-text-secondary hover:bg-[#F0FDFD]">
              <i className="ri-arrow-right-s-line" />
            </button>
            <span className="text-sm text-ghasla-text-primary">1</span>
            <button type="button" className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-lg border border-[#E4EFEF] text-ghasla-text-secondary hover:bg-[#F0FDFD]">
              <i className="ri-arrow-left-s-line" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div className="flex items-center justify-between rounded-xl bg-[#F0FDFD] px-4 py-3">
      <span className="text-ghasla-text-secondary">{label}</span>
      <span className="font-semibold text-ghasla-text-heading" style={color ? { color } : undefined}>
        {value}
      </span>
    </div>
  );
}